/* eslint-disable react-refresh/only-export-components -- POPUP_PANEL_ID is co-exported with the component. */
import {UpsellDialogEntryPoint} from '@Libraries/upsell-dialog.library';
import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {useLatest} from 'ahooks';
import type {Block, Editor} from 'grapesjs';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {useIsEmailStockPopupOpen} from '@EmailMakerReact/hooks/use-is-email-stock-popup-open';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Dropdown} from '@Components/dropdown-v2';
import {Icon} from '@Components/icon-v2';
import {Image} from 'react-bootstrap';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {TextBlockPanel} from '@Components/email-maker-block-manager/components/text-block-panel/text-block-panel';
import {ImageBlockPanel} from '@Components/email-maker-block-manager/components/image-block-panel';
import {ElementsBlockPanel} from '@Components/email-maker-block-manager/components/elements-block-panel';
import {EmailMakerBlock} from '@Components/email-maker-block-manager/components/email-maker-block';
import {SectionsBlockPanel} from '@Components/email-maker-block-manager/components/sections-block-panel';
import {
  getAllEmailComponentTemplateCategories,
  getAllEmailComponentTemplates,
  getUserEmailComponentTemplates,
  hasUserSeenElementsBlock,
  hasUserSeenSectionsBlock,
  markUserHasSeenElementsBlock,
  markUserHasSeenSectionsBlock,
} from '@Libraries/email-component-template-library';
import type {BlockCategoryMap, EmailMakerBlockAttributes} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import {ELEMENTS_BLOCK_ANCHOR_ID, IMAGE_BLOCK_ANCHOR_ID} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import {buildBlockCategoryDataMap} from '@Components/email-maker-block-manager/email-maker-block-manager.util';
import type {BlockCategoryIntroPopupContent} from '@Components/email-maker-block-manager/components/block-category-item/block-category-item.types';
import {setCategories, setSelectedBlockPanel, setTemplates, setUserTemplates} from '@Components/email-maker-block-manager/email-maker-block-manager-reducer';
import {getAssetUrl} from '@Utils/s3.util';
import {GrapesjsEditorContext, IsMobileScreenContext} from './email-maker-block-manager-grapesjs-editor-context';
import {useAppDispatch} from '@/hooks';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {MobilePanelHeader} from '@Components/email-maker-block-manager/components/mobile-panel-header';
import {useSelectedBlockPanel} from '@Components/email-maker-block-manager/email-maker-block-manager.hooks';
import {GA4EventName} from '@Libraries/ga-events';
import {EmailMakerBannerStyle} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {BlockCategoryItem} from '@Components/email-maker-block-manager/components/block-category-item/block-category-item';
import type {ElementsSubPanel} from '@Components/email-maker-block-manager/components/elements-block-panel/elements-block-panel.types';
import {MobileBlockItem} from '@Components/email-maker-block-manager/components/mobile-block-item/mobile-block-item';
import styles from './email-maker-block-manager.module.scss';
import {isUserPremium, isUserLoggedIn} from '@Libraries/user.library';
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';
import {isModalOpen} from '@Components/modal-container';
import {AuthContext} from '@Libraries/login-signup-library';

export interface EmailMakerBlockManagerProps {
  gjsEditor: Editor;
  getIsAiEditEnabled?: () => boolean;
  hideMobileHeader?: boolean;
  onAiRedesign?: () => void;
  /** Widths at or below this render the mobile (tap-to-add, non-draggable, full-width) block UI. Defaults to the legacy 768px split; the React shell passes LARGE so the sheet stays mobile up to 992px. */
  mobileBreakpoint?: ScreenBreakpoints;
  /** Controlled Elements sub-panel state for hosts that own the panel header (see ElementBlockPanelProps). */
  elementsSubPanel?: ElementsSubPanel;
  onElementsSubPanelChange?: (subPanel: ElementsSubPanel) => void;
}

export const POPUP_PANEL_ID = 'email-maker-popup-panel-id';

const AI_EDIT_SIGNUP_SOURCE = 'email-maker-ai-edit';

const PANEL_OFFSET_LEFT = 6;
const PANEL_ARROW_OFFSET = -6;

const INTRO_POPUP_SHOW_DELAY_MS = 0;
const INTRO_POPUP_HIDE_DELAY_MS = 100;

const POPUP_TOP_BOTTOM_MARGIN = 16;
const NAVBAR_HEIGHT = 60;

const DEFAULT_BLOCK_FALLBACK_ICON = 'icon-exclamation-triangle' as const;
const AI_EDIT_BLOCK_FALLBACK_ICON = 'icon-sparkle' as const;
const UNKNOWN_BLOCK_LABEL = 'Unknown Block' as const;

export function EmailMakerBlockManager({
  gjsEditor,
  getIsAiEditEnabled,
  hideMobileHeader = false,
  onAiRedesign,
  elementsSubPanel,
  onElementsSubPanelChange,
  mobileBreakpoint = ScreenBreakpoints.MEDIUM
}: EmailMakerBlockManagerProps): ReactElement {
  const blockManager = gjsEditor.BlockManager;
  const dispatch = useAppDispatch();
  const selectedCategory = useSelectedBlockPanel();
  const showLoginDialog = !isUserLoggedIn();
  const showUpsellForAiEdit = !isUserPremium();

  const [aiEditEnabled, setAiEditEnabled] = useState<boolean>(false);
  const [hasUserSeenSections, setHasUserSeenSections] = useState<boolean>(true);
  const [hasUserSeenElements, setHasUserSeenElements] = useState<boolean>(true);
  const isMobileScreen = useIsSmallerThanBreakpointWidth(mobileBreakpoint);
  const isMobileScreenLatest = useLatest(isMobileScreen);
  const isStockMediaPopupOpen = useIsEmailStockPopupOpen();

  const BLOCK_CONSTANTS = window.PMW.emails.blocks;
  const CATEGORIES_WITH_PANEL = [BLOCK_CONSTANTS.CATEGORY_SECTIONS, BLOCK_CONSTANTS.CATEGORY_ELEMENTS, BLOCK_CONSTANTS.CATEGORY_TEXT, BLOCK_CONSTANTS.CATEGORY_IMAGE];
  const CATEGORIES_WITHOUT_PANEL: string[] = [];
  const SPECIAL_BLOCKS = [BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS, BLOCK_CONSTANTS.BLOCK_AI_EDIT];

  const categoryDataMap = buildBlockCategoryDataMap(BLOCK_CONSTANTS);

  const blocksMap = blockManager.getAll().reduce<BlockCategoryMap>((blockCategoryMap, item: Block) => {
    const category = item.getCategoryLabel();
    const categoryItem = blockCategoryMap[category];

    if (!categoryItem) {
      blockCategoryMap[category] = [item];
    } else {
      categoryItem.push(item);
    }

    return blockCategoryMap;
  }, {});

  Object.keys(blocksMap).forEach((category) => {
    blocksMap[category].sort((block1, block2) => {
      const attrs1 = block1.get('attributes') as EmailMakerBlockAttributes | undefined;
      const attrs2 = block2.get('attributes') as EmailMakerBlockAttributes | undefined;
      const sortPriority1 = attrs1?.sortPriority ?? 0;
      const sortPriority2 = attrs2?.sortPriority ?? 0;
      return sortPriority1 - sortPriority2;
    });
  });

  const getTextLabel = (category: string): string => {
    if (category === BLOCK_CONSTANTS.BLOCK_AI_EDIT && onAiRedesign) {
      return window.i18next.t('pmwjs_ai_redesign');
    }

    const data = categoryDataMap[category];
    if (!data) {
      return UNKNOWN_BLOCK_LABEL;
    }

    return window.i18next.t(data.labelKey);
  };

  const getDescription = (category: string): string => {
    const data = categoryDataMap[category];
    if (!data) {
      return UNKNOWN_BLOCK_LABEL;
    }

    return window.i18next.t(data.descriptionKey);
  };

  const getIntroPopupContent = (category: string): BlockCategoryIntroPopupContent | null => {
    const data = categoryDataMap[category];
    if (!data?.introImageFilename || !data.introTextKey) {
      return null;
    }

    return {
      image: <Image src={getAssetUrl(data.introImageFilename)} alt={window.i18next.t('pmwjs_image')} aria-hidden />,
      description: <Text val={window.i18next.t(data.introTextKey)} size={TextSize.XSMALL} />,
    };
  };

  const getActivityIndicatorVisible = (category: string): boolean => {
    return (category === BLOCK_CONSTANTS.CATEGORY_SECTIONS && !hasUserSeenSections) || (category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS && !hasUserSeenElements);
  };

  const closePanelHandler = (): void => {
    dispatch(setSelectedBlockPanel(null));
  };

  const shouldPanelShowBackButton = (): boolean => isMobileScreen && !hideMobileHeader;

  const getPanelContent = (category: string | null): ReactElement => {
    const props = {
      blocksMap,
      showBackButton: shouldPanelShowBackButton(),
      onBackButton: closePanelHandler,
      gjsEditor,
    };
    const headerLabel = (key: string): string | undefined => (hideMobileHeader ? undefined : window.i18next.t(key));

    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        return <SectionsBlockPanel {...props} headerLabel={headerLabel('pmwjs_sections')} />;
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        return <ElementsBlockPanel {...props} headerLabel={headerLabel('pmwjs_elements')} subPanel={elementsSubPanel} onSubPanelChange={onElementsSubPanelChange} />;
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        return <TextBlockPanel {...props} headerLabel={isMobileScreen ? headerLabel('pmwjs_text') : undefined} />;
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        return <ImageBlockPanel {...props} headerLabel={headerLabel('pmwjs_image')} />;
      default:
        return <div>{isMobileScreen ? getDefaultMobilePanel() : <> Panel Content not defined </>}</div>;
    }
  };

  const getBlockWithoutPanel = (category: string): ReactElement => {
    return (
      <>
        {blocksMap[category].map((block) => {
          return (
            <EmailMakerBlock block={block} extraClasses={`${styles.block} spacing-p-0`} key={block.getId()}>
              {getSelectorComponent(block.getCategoryLabel())}
            </EmailMakerBlock>
          );
        })}
      </>
    );
  };

  const getBlockWithPanel = (category: string): ReactElement => {
    const popupClasses = `${styles.popup} ${category === BLOCK_CONSTANTS.CATEGORY_SECTIONS ? styles.sectionsPopup : ''} ${category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS ? styles.elementsPopup : ''}`;
    const popup = <div className={popupClasses}>{getPanelContent(category)}</div>;

    const openHandler = (): void => {
      dispatch(setSelectedBlockPanel(category));
      markSectionAsSeen(category);
      trackGA4EventOnPanelOpen(category);
    };

    return (
      <Dropdown
        open={selectedCategory === category}
        doCloseOnInnerClick={false}
        doesCloseOnOuterClick={false}
        alignment={ALIGNMENT_TYPE.RIGHT}
        position={DROPDOWN_POSITION.ABSOLUTE}
        selector={getSelectorComponent(category, styles.subSectionBlock)}
        popup={popup}
        maxHeight={window.innerHeight - NAVBAR_HEIGHT - 2 * POPUP_TOP_BOTTOM_MARGIN}
        leftOffset={PANEL_OFFSET_LEFT}
        arrowOffset={PANEL_ARROW_OFFSET}
        popUpHasCustomWidth
        useDefaultArrow
        arrowClasses={styles.popupPanelArrow}
        popupClassName={`${styles.popupPanel} ${category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS ? styles.dynamicHeight : ''}`}
        popupId={POPUP_PANEL_ID}
        popupSpacingClass={styles.popupPanelSpacing}
        key={category}
        onOpen={openHandler}
        onClose={closePanelHandler}
      />
    );
  };

  const getSpecialBlock = (category: string): ReactElement<null, typeof ClickableDiv> | null => {
    switch (category) {
      case BLOCK_CONSTANTS.BLOCK_AI_EDIT:
        return aiEditBlock();
      case BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS:
        return myDesignsBlock();

      default:
        return null;
    }
  };

  const isAiEditEnabled = (): boolean => getIsAiEditEnabled?.() ?? window.PMW.emails.AI_EDIT_ENABLED;

  const aiEditClickHandler = (): void => {
    if (onAiRedesign) {
      onAiRedesign();
      return;
    }

    if (showLoginDialog) {
      openAuthenticateModal({
        fbPermissions: '',
        signupSource: AI_EDIT_SIGNUP_SOURCE,
        authContext: AuthContext.EMAIL_MAKER,
        onLoginSuccess: async () => {
          await gjsEditor.runCommand(window.PMW.emails.commands.SAVE_TO_DB_AND_CACHE);
          window.location.reload();
        },
      });

      return;
    }

    if (showUpsellForAiEdit) {
      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT, {entryPoint: UpsellDialogEntryPoint.EMAIL_MAKER_BLOCK_MANAGER});

      return;
    }

    const isCurrentlyEnabled = isAiEditEnabled();
    gjsEditor.runCommand(window.PMW.emails.commands.TOGGLE_AI_EDIT, {enable: !isCurrentlyEnabled});
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_AI_EDIT_CLICKED);
    setAiEditEnabled(!isCurrentlyEnabled);
  };

  const aiEditBlock = (): ReactElement<null, typeof ClickableDiv> => {
    const isAiEditBlockEnabled = isAiEditEnabled();
    const extraClasses = `${styles.aiEditBlock} ${isAiEditBlockEnabled ? styles.selected : ''}`;

    return (
      <ClickableDiv key={BLOCK_CONSTANTS.BLOCK_AI_EDIT} onClick={aiEditClickHandler} className={styles.aiEditBlockContainer}>
        {isAiEditBlockEnabled ? <div className={styles.aiEditSelectedBackground} /> : null}
        {getSelectorComponent(BLOCK_CONSTANTS.BLOCK_AI_EDIT, extraClasses, styles.aiTextColor)}
        {showUpsellForAiEdit ? (
          <Icon icon="icon-crown" type={IconType.PREMIUM} shape={IconShape.NONE} size={IconSize.SIZE_ICON_20} className={`${styles.premiumTagIcon} ${styles.premiumTagDesktop}`} />
        ) : null}
      </ClickableDiv>
    );
  };

  const openDesignSelector = (): void => {
    dispatch(setSelectedBlockPanel(null));
    gjsEditor.runCommand(window.PMW.emails.commands.OPEN_DESIGN_SELECTOR);
  };

  const myDesignsBlock = (): ReactElement<null, typeof ClickableDiv> => {
    return (
      <ClickableDiv key={BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS} onClick={openDesignSelector} className={`${styles.block} ${styles.myDesignsBlock} spacing-p-0`}>
        {getSelectorComponent(BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS)}
      </ClickableDiv>
    );
  };

  const getMobileBlockItemCore = (category: string): ReactElement => {
    const data = categoryDataMap[category];
    return <MobileBlockItem icon={data?.icon ?? DEFAULT_BLOCK_FALLBACK_ICON} label={getTextLabel(category)} description={getDescription(category)} />;
  };

  const getMobileBlockWithPanel = (category: string): ReactElement => {
    return (
      <ClickableDiv
        key={category}
        onClick={() => {
          dispatch(setSelectedBlockPanel(category));
        }}
      >
        {getMobileBlockItemCore(category)}
      </ClickableDiv>
    );
  };

  const getMobileBlockWithoutPanel = (category: string): ReactElement => {
    return (
      <>
        {blocksMap[category].map((block) => {
          return (
            <EmailMakerBlock block={block} key={block.getId()}>
              {getMobileBlockItemCore(category)}
            </EmailMakerBlock>
          );
        })}
      </>
    );
  };

  const getMobileMyDesignsBlock = (): ReactElement<null, typeof ClickableDiv> => {
    return (
      <ClickableDiv key={BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS} onClick={openDesignSelector}>
        {getMobileBlockItemCore(BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS)}
      </ClickableDiv>
    );
  };

  const getMobileSpecialBlock = (category: string): ReactElement<null, typeof ClickableDiv> | null => {
    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS:
        return getMobileMyDesignsBlock();
      case BLOCK_CONSTANTS.BLOCK_AI_EDIT:
        return getMobileAiEditBlock();
      default:
        return null;
    }
  };

  const getMobileAiEditBlock = (): ReactElement<null, typeof ClickableDiv> => {
    const data = categoryDataMap[BLOCK_CONSTANTS.BLOCK_AI_EDIT];
    const bannerProps = {
      style: EmailMakerBannerStyle.AI,
      message: window.i18next.t('pmwjs_ai_edit_now_enabled'),
      icon: 'icon-sparkle',
      ctaText: window.i18next.t('pmwjs_turn_off_ai_edit'),
      cta: (): void => {
        gjsEditor.runCommand(window.PMW.emails.commands.HIDE_BANNER);
        gjsEditor.runCommand(window.PMW.emails.commands.TOGGLE_AI_EDIT, {enable: false});
        setAiEditEnabled(false);
      },
    };

    const buttonCta = (): void => {
      if (!onAiRedesign) {
        if (!isAiEditEnabled()) {
          gjsEditor.runCommand(window.PMW.emails.commands.SHOW_BANNER, bannerProps);
        } else {
          gjsEditor.runCommand(window.PMW.emails.commands.HIDE_BANNER);
        }
      }

      gjsEditor.runCommand(window.PMW.emails.commands.CLOSE_ADD_BLOCKS_DIALOG);
      aiEditClickHandler();
    };

    const containerClasses = `${styles.aiEditBlock} ${aiEditEnabled ? styles.selected : ''}`;

    return (
      <ClickableDiv key={BLOCK_CONSTANTS.BLOCK_AI_EDIT} onClick={buttonCta}>
        <MobileBlockItem
          icon={data?.icon ?? AI_EDIT_BLOCK_FALLBACK_ICON}
          label={getTextLabel(BLOCK_CONSTANTS.BLOCK_AI_EDIT)}
          description={getDescription(BLOCK_CONSTANTS.BLOCK_AI_EDIT)}
          containerClasses={containerClasses}
          iconContainerClasses={styles.aiEditIconContainer}
          textClasses={styles.aiTextColor}
        />
        {showUpsellForAiEdit ? (
          <Icon icon="icon-crown" type={IconType.PREMIUM} shape={IconShape.NONE} size={IconSize.SIZE_ICON_20} className={`${styles.premiumTagIcon} ${styles.premiumTagMobile}`} />
        ) : null}
      </ClickableDiv>
    );
  };

  // The stock-media popup points its arrow at the tile a flow originated from, so these tiles carry stable ids.
  const getCategoryAnchorId = (category: string): string | undefined => {
    if (category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS) {
      return ELEMENTS_BLOCK_ANCHOR_ID;
    }
    if (category === BLOCK_CONSTANTS.CATEGORY_IMAGE) {
      return IMAGE_BLOCK_ANCHOR_ID;
    }
    return undefined;
  };

  const getSelectorComponent = (category: string, extraClasses = '', textClasses = ''): ReactElement => {
    const data = categoryDataMap[category];
    return (
      <BlockCategoryItem
        id={getCategoryAnchorId(category)}
        icon={data?.icon ?? DEFAULT_BLOCK_FALLBACK_ICON}
        label={getTextLabel(category)}
        isSelected={selectedCategory === category}
        isAnyPanelOpen={Boolean(selectedCategory) || isStockMediaPopupOpen}
        blockClasses={extraClasses}
        textClasses={textClasses}
        showActivityIndicator={getActivityIndicatorVisible(category)}
        introPopupContent={getIntroPopupContent(category)}
        introPopupShowDelay={INTRO_POPUP_SHOW_DELAY_MS}
        introPopupHideDelay={INTRO_POPUP_HIDE_DELAY_MS}
      />
    );
  };

  const getDefaultMobilePanelHeader = (): ReactElement | null => {
    if (hideMobileHeader) {
      return null;
    }
    return (
      <div className={styles.mobilePanelHeader}>
        <MobilePanelHeader headerLabel={window.i18next.t('pmwjs_add_to_email')} />
      </div>
    );
  };

  const getDefaultMobilePanel = (): ReactElement => {
    return (
      <>
        {getDefaultMobilePanelHeader()}
        <div className={styles.mobileAllBlocks}>{getAllBlocks()}</div>
      </>
    );
  };

  const getAllBlocks = (): ReactElement => {
    const blockWithPanel = (category: string): ReactElement => {
      if (isMobileScreen) {
        return getMobileBlockWithPanel(category);
      }

      return getBlockWithPanel(category);
    };

    const blockWithoutPanel = (category: string): ReactElement => {
      if (isMobileScreen) {
        return getMobileBlockWithoutPanel(category);
      }

      return getBlockWithoutPanel(category);
    };

    const specialBlock = (category: string): ReactElement | null => {
      if (isMobileScreen) {
        return getMobileSpecialBlock(category);
      }

      return getSpecialBlock(category);
    };

    return (
      <>
        {CATEGORIES_WITH_PANEL.map((category) => {
          return blockWithPanel(category);
        })}
        {CATEGORIES_WITHOUT_PANEL.map((category) => {
          return blockWithoutPanel(category);
        })}
        {SPECIAL_BLOCKS.map((category) => {
          return specialBlock(category);
        })}
      </>
    );
  };

  const markSectionAsSeen = (category: string): void => {
    if (category === BLOCK_CONSTANTS.CATEGORY_SECTIONS && !hasUserSeenSections) {
      setHasUserSeenSections(true);
      void markUserHasSeenSectionsBlock();
    } else if (category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS && !hasUserSeenElements) {
      setHasUserSeenElements(true);
      void markUserHasSeenElementsBlock();
    }
  };

  const fetchAllDataIntoRedux = async (): Promise<void> => {
    await Promise.all([getAllEmailComponentTemplateCategories(), getAllEmailComponentTemplates(), getUserEmailComponentTemplates()]).then(
      ([categories, templates, userTemplates]) => {
        dispatch(setCategories(categories));
        dispatch(setTemplates(templates));
        dispatch(setUserTemplates(userTemplates));
      }
    );
  };

  const closePopupListener = (e: MouseEvent | TouchEvent): void => {
    if (isMobileScreenLatest.current) {
      return;
    }

    const target = e.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    if (!document.getElementById(window.PMW.emails.blocks.ID_BLOCKS_MANAGER_CONTAINER)?.contains(target)) {
      closePanelHandler();
    }
  };

  const trackGA4EventOnPanelOpen = (category: string): void => {
    if (category === BLOCK_CONSTANTS.CATEGORY_SECTIONS) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_ADD_SECTION);
    }
  };

  const aiEditClickOutsideListener = (event: MouseEvent | TouchEvent): void => {
    if (isMobileScreenLatest.current) {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const isAiImagesModalOpen = isModalOpen();
    const editorContainer = gjsEditor.getContainer();
    const clickedOnCanvas = editorContainer?.contains(target);

    if (!clickedOnCanvas && !isAiImagesModalOpen && aiEditEnabled) {
      gjsEditor.runCommand(window.PMW.emails.commands.TOGGLE_AI_EDIT, {enable: false});
      setAiEditEnabled(false);
    }
  };

  const aiEditKeydownHandler = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && aiEditEnabled) {
      gjsEditor.runCommand(window.PMW.emails.commands.TOGGLE_AI_EDIT, {enable: false});
      setAiEditEnabled(false);
    }
  };

  useEffect(() => {
    void hasUserSeenSectionsBlock().then((hasSeen) => {
      setHasUserSeenSections(hasSeen);
    });
    void hasUserSeenElementsBlock().then((hasSeen) => {
      setHasUserSeenElements(hasSeen);
    });
    void fetchAllDataIntoRedux();

    document.addEventListener('touchstart', closePopupListener);
    document.addEventListener('mousedown', closePopupListener);

    return (): void => {
      document.removeEventListener('touchstart', closePopupListener);
      document.removeEventListener('mousedown', closePopupListener);
    };
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', aiEditKeydownHandler);
    window.addEventListener('mousedown', aiEditClickOutsideListener);

    return (): void => {
      document.removeEventListener('keydown', aiEditKeydownHandler);
      window.removeEventListener('mousedown', aiEditClickOutsideListener);
    };
  }, [aiEditEnabled]);

  const getBlockManager = (): ReactElement => {
    if (isMobileScreen) {
      return <IsMobileScreenContext.Provider value={isMobileScreen}>{getPanelContent(selectedCategory)}</IsMobileScreenContext.Provider>;
    }

    return <div className={styles.blocksManager}>{getAllBlocks()}</div>;
  };

  return <GrapesjsEditorContext.Provider value={gjsEditor}>{getBlockManager()}</GrapesjsEditorContext.Provider>;
}
